/**
 * Знак ANZONIX — «Огранка».
 *
 * Камень-ромб с гранями и мятной искрой и название своими буквами:
 * тонкими, с переливом, буква A без перекладины, у X восходящая черта
 * мятная. Раньше знаком был ромб из стилей и слово обычным шрифтом —
 * приятно, но спутать можно было с десятком других. Теперь у знака две
 * детали, по которым его узнают: искра у камня и мятная черта в конце
 * слова.
 *
 * Размер задаёт размер шрифта родителя: камень и слово измеряются в `em`,
 * поэтому прежние правила «крупнее на входе, мельче в строке меню»
 * продолжают работать без правок.
 */

import type { JSX } from 'react';
import './brand.css';

const MINT = '#2ee6b0';

/** Буквы названия штрихами в клетке высотой 46: ширина и путь. */
const LETTERS: ReadonlyArray<readonly [number, string]> = [
  [36, 'M3,44 L18,3 L33,44'],
  [34, 'M4,44 L4,3 L30,44 L30,3'],
  [34, 'M4,3 L30,3 L4,44 L30,44'],
  [42, 'M21,3 a20.5,20.5 0 1 0 0.01,0 Z'],
  [34, 'M4,44 L4,3 L30,44 L30,3'],
  [8, 'M4,3 L4,44'],
  [34, 'M4,3 L30,44'],
];
/** Просвет между буквами и толщина штриха. */
const TRACK = 23;
const WEIGHT = 5;
const WORD_WIDTH = LETTERS.reduce((sum, [width]) => sum + width, 0) + TRACK * (LETTERS.length - 1);

/** Четырёхлучевая искра с центром в точке и заданным размахом. */
function sparkPath(x: number, y: number, r: number): string {
  const k = r * 0.16;
  return `M${x},${y - r} Q${x + k},${y - k} ${x + r},${y} Q${x + k},${y + k} ${x},${y + r} Q${x - k},${y + k} ${x - r},${y} Q${x - k},${y - k} ${x},${y - r} Z`;
}

/**
 * Камень. `plain` — упрощённый, для мелких размеров: без граней и с искрой
 * покрупнее. В 16–30 пикселях грани превращаются в шум, а искра теряется.
 */
export function BrandGem({ plain = false, appear = false }: { plain?: boolean; appear?: boolean }): JSX.Element {
  return (
    <svg className={appear ? 'brand-gem brand-gem--appear' : 'brand-gem'} viewBox="0 0 64 64" aria-hidden="true">
      <defs>
        <linearGradient id="anx-gem" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#c084fc" />
          <stop offset="0.45" stopColor="#7c3aed" />
          <stop offset="1" stopColor="#22d3ee" />
        </linearGradient>
        <linearGradient id="anx-gloss" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#ffffff" stopOpacity="0.42" />
          <stop offset="0.45" stopColor="#ffffff" stopOpacity="0" />
        </linearGradient>
        <filter id="anx-glow" x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur stdDeviation="3.2" />
        </filter>
      </defs>
      <g className="brand-gem-body">
        <g transform="rotate(45 32 32)">
          <rect x="15" y="15" width="34" height="34" rx="10" fill="#a78bfa" opacity="0.55" filter="url(#anx-glow)" />
          <rect x="15" y="15" width="34" height="34" rx="10" fill="url(#anx-gem)" />
          <rect x="15" y="15" width="34" height="34" rx="10" fill="url(#anx-gloss)" />
          <rect x="15.6" y="15.6" width="32.8" height="32.8" rx="9.4" fill="none" stroke="#ffffff" strokeOpacity="0.28" strokeWidth="1.2" />
          {!plain && (
            <>
              <rect x="24" y="24" width="16" height="16" rx="2" fill="#ffffff" fillOpacity="0.1" stroke="#ffffff" strokeOpacity="0.45" strokeWidth="1" />
              <path d="M17.5,17.5 L24,24 M46.5,17.5 L40,24 M46.5,46.5 L40,40 M17.5,46.5 L24,40" stroke="#ffffff" strokeOpacity="0.4" strokeWidth="1" strokeLinecap="round" />
            </>
          )}
        </g>
      </g>
      <path className="brand-gem-spark" d={sparkPath(52, 13, plain ? 8.8 : 7.5)} fill={MINT} />
    </svg>
  );
}

/** Название: свои буквы с переливом, у последней X восходящая черта мятная. */
export function BrandWord(): JSX.Element {
  let x = 0;
  return (
    <svg className="brand-word" viewBox={`-6 -8 ${WORD_WIDTH + 12} 62`} aria-hidden="true">
      <defs>
        <linearGradient id="anx-word" gradientUnits="userSpaceOnUse" x1="0" y1="0" x2={WORD_WIDTH} y2="0">
          <stop offset="0" stopColor="#c084fc" />
          <stop offset="0.42" stopColor="#a78bfa" />
          <stop offset="0.78" stopColor="#7dd3fc" />
          <stop offset="1" stopColor="#67e8f9" />
        </linearGradient>
      </defs>
      <g fill="none" strokeWidth={WEIGHT} strokeLinecap="round" strokeLinejoin="round">
        {LETTERS.map(([width, path], index) => {
          const at = x;
          x += width + TRACK;
          return (
            <g key={index} transform={`translate(${at} 0)`}>
              <path d={path} stroke="url(#anx-word)" />
              {index === LETTERS.length - 1 && <path d="M4,44 L30,3" stroke={MINT} />}
            </g>
          );
        })}
      </g>
    </svg>
  );
}

/**
 * Знак целиком. `row` — камень слева от названия, для строки меню: камень
 * там мелкий, поэтому упрощённый. `stack` — камень над названием, для
 * экрана входа: крупно и с гранями.
 */
export function BrandLogo({ layout = 'row' }: { layout?: 'row' | 'stack' }): JSX.Element {
  return (
    <span className={`brand brand--${layout}`} role="img" aria-label="ANZONIX">
      <BrandGem plain={layout === 'row'} appear />
      <BrandWord />
    </span>
  );
}
